import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Video, Search, X, ChevronRight, ChevronDown, List } from 'lucide-react';
import PageWrapper from '../components/PageWrapper';
import VideoCard from '../components/VideoCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VideoLibrary = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [showAllExpanded, setShowAllExpanded] = useState(false);

  // Categories for filtering - using actual database categories
  const categories = [
    { 
      id: 'Kidney Stones', 
      name: 'Kidney Stones', 
      description: 'Stone removal procedures and prevention'
    },
    { 
      id: 'Kidney & Upper Tract', 
      name: 'Kidney & Upper Tract', 
      description: 'Kidney cancer, renal masses, nephrostomy care'
    },
    { 
      id: 'Prostate (Non-Cancer)', 
      name: 'Prostate (Non-Cancer)', 
      description: 'BPH treatments: TURP, HoLEP, Rezum, UroLift'
    },
    { 
      id: 'Prostate Cancer', 
      name: 'Prostate Cancer', 
      description: 'Biopsy, prostatectomy, radiation, active surveillance'
    },
    { 
      id: 'Bladder & Voiding', 
      name: 'Bladder & Voiding', 
      description: 'Bladder cancer, TURBT, BCG, cystoscopy, catheter care'
    },
    { 
      id: "Men's Sexual & Reproductive", 
      name: "Men's Sexual & Reproductive", 
      description: 'ED, testosterone, Peyronie\'s, varicocele, vasectomy'
    },
    { 
      id: "Women's Pelvic Health", 
      name: "Women's Pelvic Health", 
      description: 'GSM/menopause, prolapse, pelvic floor exercises'
    },
    { 
      id: 'Pediatric Urology', 
      name: 'Pediatric Urology', 
      description: 'Pediatric conditions and transitioning to adult care'
    },
    { 
      id: 'Infections & General', 
      name: 'Infections & General', 
      description: 'UTIs, HPV/genital warts, general education'
    }
  ];

  useEffect(() => {
    const loadVideos = async () => {
      try {
        const response = await axios.get(`${API}/videos?limit=100`);
        setVideos(response.data);
      } catch (error) {
        console.error("Error loading videos:", error);
      } finally {
        setLoading(false);
      }
    };
    loadVideos();
  }, []);

  // Get videos for a specific category - now using actual category field
  const getVideosForCategory = (categoryId) => {
    return videos
      .filter(v => v.category === categoryId)
      .sort((a, b) => a.title.localeCompare(b.title));
  };

  // Filter all videos by search
  const filteredVideos = useMemo(() => {
    if (!searchQuery.trim()) return videos;
    
    const query = searchQuery.toLowerCase();
    return videos.filter(v => 
      v.title.toLowerCase().includes(query) ||
      v.description?.toLowerCase().includes(query) ||
      v.tags?.some(tag => tag.toLowerCase().includes(query)) ||
      v.source?.toLowerCase().includes(query)
    );
  }, [videos, searchQuery]);

  // Group videos alphabetically for "Show All" view
  const groupedVideos = useMemo(() => {
    const sorted = [...filteredVideos].sort((a, b) => a.title.localeCompare(b.title));
    const groups = {};
    sorted.forEach(video => {
      const firstLetter = video.title.charAt(0).toUpperCase();
      if (!groups[firstLetter]) {
        groups[firstLetter] = [];
      }
      groups[firstLetter].push(video);
    });
    return groups;
  }, [filteredVideos]);

  // Toggle category expansion
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // Expand all categories
  const expandAll = () => {
    const allIds = categories.map(c => c.id);
    setExpandedCategories(new Set(allIds));
    setShowAllExpanded(true);
  };

  // Collapse all categories
  const collapseAll = () => {
    setExpandedCategories(new Set());
    setShowAllExpanded(false);
  };

  // Check if searching
  const isSearching = searchQuery.trim().length > 0;

  if (loading) {
    return (
      <PageWrapper>
        <div className="loading-state" data-testid="loading-state">
          <div className="spinner"></div>
          <p>Loading videos...</p>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      {/* Header */}
      <div className="video-library-header" data-testid="video-library-header">
        <div className="video-library-title">
          <Video size={28} />
          <div>
            <h1>Video Library</h1>
            <p>Educational videos from trusted medical sources</p>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="video-search-section">
        <div className="video-search-wrapper">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search videos..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="video-search-input"
            data-testid="video-search-input"
          />
          {searchQuery && (
            <button 
              className="video-search-clear"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Results Count & Controls */}
      <div className="video-toolbar" data-testid="video-toolbar">
        <div className="video-results-count" data-testid="results-count">
          {filteredVideos.length} video{filteredVideos.length !== 1 ? 's' : ''}
          {searchQuery && ` for "${searchQuery}"`}
        </div>
        {!isSearching && (
          <div className="accordion-controls">
            <button onClick={expandAll} className="accordion-control-btn">
              Expand All
            </button>
            <button onClick={collapseAll} className="accordion-control-btn">
              Collapse All
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      {isSearching ? (
        /* Search Results - A-Z List */
        <div className="videos-az-view" data-testid="videos-search-results">
          {Object.keys(groupedVideos).length > 0 ? (
            Object.keys(groupedVideos).sort().map(letter => (
              <div key={letter} className="az-group">
                <h3 className="az-letter">{letter}</h3>
                <div className="az-items">
                  {groupedVideos[letter].map(video => (
                    <VideoCard key={video.id} video={video} compact={true} />
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state" data-testid="empty-state">
              <Video size={48} />
              <h3>No videos found</h3>
              <p>Try a different search term</p>
              <button 
                className="btn-primary" 
                onClick={() => setSearchQuery('')}
              >
                View All Videos
              </button>
            </div>
          )}
        </div>
      ) : (
        /* Categorized Accordion View */
        <div className="conditions-accordion video-accordion" data-testid="video-accordion">
          {/* Show All Videos Accordion */}
          <div className="accordion-section accordion-show-all" data-testid="accordion-show-all">
            <button
              className={`accordion-header ${showAllExpanded ? 'expanded' : ''}`}
              onClick={() => setShowAllExpanded(!showAllExpanded)}
              data-testid="accordion-header-show-all"
            >
              <div className="accordion-header-left">
                <List size={22} className="accordion-icon" />
                <div className="accordion-title-group">
                  <h3>Show All Videos (A-Z)</h3>
                  <span className="accordion-description">Browse all videos alphabetically</span>
                </div>
              </div>
              <div className="accordion-header-right">
                <span className="accordion-count">{videos.length}</span>
                {showAllExpanded ? (
                  <ChevronDown size={20} className="accordion-chevron" />
                ) : (
                  <ChevronRight size={20} className="accordion-chevron" />
                )}
              </div>
            </button>
            
            {showAllExpanded && (
              <div className="accordion-content accordion-content-videos" data-testid="accordion-content-show-all">
                <div className="videos-az-view">
                  {Object.keys(groupedVideos).sort().map(letter => (
                    <div key={letter} className="az-group">
                      <h3 className="az-letter">{letter}</h3>
                      <div className="az-items">
                        {groupedVideos[letter].map(video => (
                          <VideoCard key={video.id} video={video} compact={true} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Category Accordions */}
          {categories.map(cat => {
            const categoryVideos = getVideosForCategory(cat.id);
            const count = categoryVideos.length;
            const isExpanded = expandedCategories.has(cat.id);
            
            if (count === 0) return null;
            
            return (
              <div key={cat.id} className="accordion-section" data-testid={`accordion-${cat.id}`}>
                <button
                  className={`accordion-header ${isExpanded ? 'expanded' : ''}`}
                  onClick={() => toggleCategory(cat.id)}
                  data-testid={`accordion-header-${cat.id}`}
                >
                  <div className="accordion-header-left">
                    <Video size={22} className="accordion-icon" />
                    <div className="accordion-title-group">
                      <h3>{cat.name}</h3>
                      <span className="accordion-description">{cat.description}</span>
                    </div>
                  </div>
                  <div className="accordion-header-right">
                    <span className="accordion-count">{count}</span>
                    {isExpanded ? (
                      <ChevronDown size={20} className="accordion-chevron" />
                    ) : (
                      <ChevronRight size={20} className="accordion-chevron" />
                    )}
                  </div>
                </button>
                
                {isExpanded && (
                  <div className="accordion-content accordion-content-videos" data-testid={`accordion-content-${cat.id}`}>
                    <div className="video-category-grid">
                      {categoryVideos.map(video => (
                        <VideoCard key={video.id} video={video} compact={true} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Disclaimer */}
      <div className="video-library-disclaimer" data-testid="video-disclaimer">
        <p>These videos are provided as educational resources from external organizations including Mayo Clinic, Cleveland Clinic, and the Urology Care Foundation. Uro2Go does not create or control this content.</p>
      </div>
    </PageWrapper>
  );
};

export default VideoLibrary;
